import { supabase } from "@/integrations/supabase/client";
import { isAgentHandoffStage } from "@/lib/lead-stage-agent-handoff";
import { fetchLeadFunnelPlacement, persistLeadFunnelPlacement } from "@/lib/lead-funnel-placement";
import { dispatchConversationLeadPatch } from "@/lib/conversation-lead-patch";
import {
  dispatchConversationInboxBadgeSync,
  dispatchConversationInboxRowPatch,
} from "@/lib/conversation-inbox-patch";

export type ResolveEscalationResult = {
  ok: boolean;
  resolved: number;
  error?: string;
  agentReactivated?: boolean;
  agentBlocked?: boolean;
  agentError?: string;
  stageRestored?: boolean;
  restoredStageSlug?: string;
};

type EscalationErrorDetails = {
  source_stage_slug?: string;
  source_funnel_id?: string | null;
};

/**
 * Lê estágio de origem salvo no evento de escalação pendente (antes de marcar como resolvido).
 */
export async function fetchPendingEscalationRestoreTarget(
  leadId: string,
): Promise<{ funnelId: string | null; stageSlug: string } | null> {
  const { data, error } = await supabase
    .from("lead_escalation_events")
    .select("error_details")
    .eq("lead_id", leadId)
    .is("resolved_at", null)
    .order("created_at", { ascending: false });

  if (error || !data?.length) return null;

  for (const row of data) {
    const details = (row.error_details || {}) as EscalationErrorDetails;
    const stageSlug = typeof details.source_stage_slug === "string" ? details.source_stage_slug.trim() : "";
    if (!stageSlug) continue;
    const funnelId =
      typeof details.source_funnel_id === "string" && details.source_funnel_id.trim()
        ? details.source_funnel_id.trim()
        : null;
    return { funnelId, stageSlug };
  }

  return null;
}

async function inferFallbackRestoreStage(
  leadId: string,
  workspaceId: string,
  preferIntegrationId?: string | null,
): Promise<{ funnelId: string; stageSlug: string } | null> {
  const placement = await fetchLeadFunnelPlacement(supabase, leadId, new Map(), {
    workspaceId,
    preferIntegrationId,
  });
  if (!placement?.funnelId) return null;

  const { data: stages, error } = await supabase
    .from("lead_stages")
    .select("slug, auto_disable_agent, order_index")
    .eq("funnel_id", placement.funnelId)
    .order("order_index", { ascending: true });

  if (error || !stages?.length) return null;

  const restoreStage = stages.find(
    (s) => s.auto_disable_agent !== true && s.slug !== "atendimento_humano",
  );
  if (!restoreStage?.slug) return null;

  return { funnelId: placement.funnelId, stageSlug: restoreStage.slug };
}

/**
 * Devolve o lead ao estágio anterior à escalação (quando estava em coluna de handoff).
 */
export async function restoreLeadStageAfterEscalation(
  leadId: string,
  workspaceId: string,
  options?: { preferIntegrationId?: string | null; restoreTarget?: { funnelId: string | null; stageSlug: string } | null },
): Promise<{ restored: boolean; stageSlug?: string }> {
  const placement = await fetchLeadFunnelPlacement(supabase, leadId, new Map(), {
    workspaceId,
    preferIntegrationId: options?.preferIntegrationId,
  });
  if (!placement) return { restored: false };

  const inHandoffStage =
    isAgentHandoffStage(placement.stageSlug) || placement.stageSlug === "atendimento_humano";
  if (!inHandoffStage) return { restored: false };

  const explicitTarget = options?.restoreTarget ?? (await fetchPendingEscalationRestoreTarget(leadId));
  const target =
    explicitTarget ??
    (await inferFallbackRestoreStage(leadId, workspaceId, options?.preferIntegrationId));

  if (!target?.stageSlug) return { restored: false };

  const funnelId = target.funnelId || placement.funnelId;
  if (!funnelId) return { restored: false };

  await persistLeadFunnelPlacement(supabase, leadId, funnelId, target.stageSlug);
  return { restored: true, stageSlug: target.stageSlug };
}

export type EscalationResolvedLocalSnapshot = {
  state: string;
  followup_enabled?: boolean;
  lead_stage?: string;
  stage_auto_disables_agent?: boolean;
};

/** Atualiza lista e badges sem recarregar a tela inteira. */
export function applyEscalationResolvedLocally(params: {
  conversationId: string;
  leadId: string;
  result: ResolveEscalationResult;
  previousState: string;
  previousFollowupEnabled: boolean;
  previousStage?: string;
  previousStageBlocksAgent?: boolean;
}): EscalationResolvedLocalSnapshot {
  const {
    conversationId,
    leadId,
    result,
    previousState,
    previousFollowupEnabled,
    previousStage,
    previousStageBlocksAgent,
  } = params;

  const nextState = result.agentReactivated ? "active" : previousState;
  const nextFollowup = result.agentReactivated ? true : previousFollowupEnabled;
  const nextStage = result.restoredStageSlug ?? previousStage;
  const nextStageBlocks = result.stageRestored
    ? false
    : result.agentBlocked
      ? true
      : previousStageBlocksAgent;

  dispatchConversationInboxRowPatch({
    conversationId,
    state: nextState,
    escalatedByAgentAt: null,
  });

  dispatchConversationLeadPatch({
    leadId,
    ...(nextStage ? { lead_stage: nextStage } : {}),
    ...(nextStageBlocks !== undefined
      ? {
          stage_auto_disables_agent: nextStageBlocks,
          isUrgent: nextStageBlocks,
        }
      : result.stageRestored
        ? { stage_auto_disables_agent: false, isUrgent: false }
        : {}),
  });

  dispatchConversationInboxBadgeSync({ conversationId });

  return {
    state: nextState,
    followup_enabled: nextFollowup,
    lead_stage: nextStage,
    stage_auto_disables_agent: nextStageBlocks,
  };
}
